// The outbox event catalogue [02 §5.1, §5.2; 04–07 event tables]. One entry per (type, version);
// a breaking payload change adds `<type>@<n+1>` and keeps the old version parseable.
import { AppError } from '../kernel/index.ts';
import { CHANNELS_EVENTS } from './channels.ts';
import { outboxEnvelopeSchema, type EventDefinition, type OutboxEventOf } from './define.ts';
import { OPERATIONS_EVENTS } from './operations.ts';
import { PLATFORM_EVENTS } from './platform.ts';
import { PLM_EVENTS } from './plm.ts';
import { SUPPLY_EVENTS } from './supply.ts';

export const EVENT_CATALOGUE = Object.freeze([
  ...PLM_EVENTS,
  ...SUPPLY_EVENTS,
  ...OPERATIONS_EVENTS,
  ...CHANNELS_EVENTS,
  ...PLATFORM_EVENTS,
] as const);

export type CatalogueEvent = (typeof EVENT_CATALOGUE)[number];
export type EventType = CatalogueEvent['type'];

type Distribute<D> = D extends EventDefinition ? OutboxEventOf<D> : never;
/** Discriminated union (on `event_type`) of every catalogued event. */
export type OutboxEvent = Distribute<CatalogueEvent>;

const keyOf = (type: string, version: number): string => `${type}@${version}`;

const BY_KEY: ReadonlyMap<string, CatalogueEvent> = new Map(
  EVENT_CATALOGUE.map((definition) => [keyOf(definition.type, definition.version), definition]),
);

export const EVENT_TYPES: readonly EventType[] = Object.freeze([
  ...new Set(EVENT_CATALOGUE.map((definition) => definition.type)),
]);

const TYPE_SET: ReadonlySet<string> = new Set(EVENT_TYPES);

export function isEventType(value: unknown): value is EventType {
  return typeof value === 'string' && TYPE_SET.has(value);
}

export function findEventDefinition(type: string, version: number): CatalogueEvent | undefined {
  return BY_KEY.get(keyOf(type, version));
}

/**
 * Spec spellings that differ from the canonical type (02 §5.1 summary vs the detailed chapters).
 * Canonical names follow `<context>.<aggregate>.<verb>` and the detailed chapter [lane report, Deviations].
 */
export const SPEC_EVENT_ALIASES: ReadonlyMap<string, EventType> = new Map<string, EventType>([
  ['materials.hold.set', 'materials.hold.placed'],
  ['purchasing.receipt.posted', 'purchasing.goods_receipt.posted'],
  ['costing.version_approved', 'costing.sheet.approved'],
  ['costing.actual_closed', 'costing.actual.closed'],
]);

/** Canonical type for a catalogue name or a spec alias; undefined when unknown. */
export function canonicalEventType(name: string): EventType | undefined {
  return isEventType(name) ? name : SPEC_EVENT_ALIASES.get(name);
}

/**
 * Validates an outbox row read by a consumer: envelope, known (type, version), matching aggregate
 * type, then the versioned payload. Unknown or inconsistent events are VALIDATION_FAILED; payload
 * violations throw the ZodError of the payload schema.
 */
export function parseOutboxEvent(raw: unknown): OutboxEvent {
  const envelope = outboxEnvelopeSchema.parse(raw);
  const definition = findEventDefinition(envelope.event_type, envelope.schema_version);
  if (definition === undefined || definition.aggregateType !== envelope.aggregate_type) {
    throw new AppError('VALIDATION_FAILED', {
      params: {
        eventType: envelope.event_type,
        schemaVersion: envelope.schema_version,
        aggregateType: envelope.aggregate_type,
      },
    });
  }
  const { published_at: _relayState, ...event } = envelope;
  return { ...event, payload: definition.payload.parse(envelope.payload) } as OutboxEvent;
}
